// Legacy per-user accent colors were removed for beta launch. All users now
// share the generic accent token (`var(--color-accent-text)`).
export type ViewKey = string;

export const ACCENT = "#e8ff47";

export type Unit = "kg" | "lbs";
export const KG_TO_LBS = 2.20462;
export function fromKg(kg: number, unit: Unit): number {
  return unit === "kg" ? kg : kg * KG_TO_LBS;
}
export function toKg(value: number, unit: Unit): number {
  return unit === "kg" ? value : value / KG_TO_LBS;
}
export function fmtWeight(kg: number | string | null | undefined, unit: Unit, digits = 1): string {
  if (kg == null || kg === "") return "—";
  const n = typeof kg === "string" ? parseFloat(kg) : kg;
  if (!Number.isFinite(n)) return "—";
  return `${fromKg(n, unit).toFixed(digits)}${unit}`;
}

// Height unit handling — height is always stored in cm; ft+in is display/input only.
export type HeightUnit = "cm" | "ftin";
export const CM_PER_IN = 2.54;
export const IN_PER_FT = 12;

export function cmToFtIn(cm: number): { ft: number; inch: number } {
  const totalIn = Math.round(cm / CM_PER_IN);
  const ft = Math.floor(totalIn / IN_PER_FT);
  const inch = totalIn - ft * IN_PER_FT;
  return { ft, inch };
}

export function ftInToCm(ft: number, inch: number): number {
  return Math.round((ft * IN_PER_FT + inch) * CM_PER_IN);
}

export function fmtHeight(cm: number | string | null | undefined, unit: HeightUnit): string {
  if (cm == null || cm === "") return "—";
  const n = typeof cm === "string" ? parseFloat(cm) : cm;
  if (!Number.isFinite(n)) return "—";
  if (unit === "cm") return `${Math.round(n)} cm`;
  const { ft, inch } = cmToFtIn(n);
  return `${ft}'${inch}"`;
}

export function todayISO(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function dateFromISO(iso: string): Date {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
}

export function mondayIndexFromISO(iso: string): number {
  return (dateFromISO(iso).getDay() + 6) % 7;
}

export function formatISODateLabel(iso: string): string {
  const d = dateFromISO(iso);
  const weekday = WEEKDAY_LABELS[(d.getDay() + 6) % 7];
  return `${weekday}, ${MONTH_LABELS[d.getMonth()]} ${d.getDate()}`;
}

export function formatDateEU(d: Date | string): string {
  const dt = typeof d === "string" ? new Date(d) : d;
  const day = String(dt.getDate()).padStart(2, "0");
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  return `${day}/${m}/${dt.getFullYear()}`;
}

export function formatHMS(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}
